import { useMutation } from '@tanstack/react-query'
import { Form, Formik, FormikHelpers } from 'formik'
import React from 'react'

import TextareaInput from '@/components/form/TextareaInput'
import ActionButton from '@/components/misc/ActionButton'
import { useInvalidateCommentQueries } from '@/components/misc/Comments/hooks/useInvalidateCommentQueries'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useHandleApiError } from '@/hooks/useHandleApiError'
import useNotyf from '@/hooks/useNotyf'
import commentService from '@/lib/services/commentService'
import { CommentEntityAlias } from '@/types/misc'
import { CommentCollectionItem } from '@/types/responses/comments'

interface Props {
  entity: CommentEntityAlias
  entityId: number
  comment?: CommentCollectionItem | null
  onAdd?: () => Promise<unknown>
  onHide: () => void
}

interface AddOrUpdateCommentFormValues {
  body: string
}

const AddCommentOrUpdateFormFields: Record<
  keyof AddOrUpdateCommentFormValues,
  keyof AddOrUpdateCommentFormValues
> = {
  body: 'body',
}

const AddOrUpdateCommentModal: React.FC<Props> = ({
  entity,
  entityId,
  comment,
  onAdd,
  onHide,
}) => {
  const notyf = useNotyf()
  const initialValues: AddOrUpdateCommentFormValues = {
    body: comment?.body || '',
  }
  const handleApiError = useHandleApiError()
  const invalidateCommentQueries = useInvalidateCommentQueries()
  const isUpdating = !!comment

  const createOrUpdateCommentMutation = useMutation({
    mutationFn: async ({
      values,
    }: {
      values: AddOrUpdateCommentFormValues
      formikHelpers: FormikHelpers<AddOrUpdateCommentFormValues>
    }) => {
      const body = values.body

      return isUpdating
        ? commentService.updateComment(comment.id, { body })
        : commentService.createComment({
            entityAlias: entity,
            entityId,
            body,
          })
    },
    onSuccess: handleAddOrUpdateSuccess,
    onError: (error, { formikHelpers }) =>
      handleApiError({ error, formikHelpers }),
  })

  async function handleAddOrUpdateSuccess() {
    if (onAdd) {
      await onAdd()
    }

    await invalidateCommentQueries()
    onHide()

    notyf.success(`Comment successfully ${isUpdating ? 'updated' : 'added'}`)
  }

  const isProcessing = createOrUpdateCommentMutation.isPending

  return (
    <Dialog open onHide={onHide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isUpdating ? 'Update' : 'Add'} comment</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          onSubmit={(values, formikHelpers) =>
            createOrUpdateCommentMutation.mutate({ values, formikHelpers })
          }
        >
          <Form className="flex flex-col gap-3">
            <DialogBody>
              <TextareaInput
                name={AddCommentOrUpdateFormFields.body}
                placeholder="Add comment"
                rows={5}
                autoFocus
                isDisabled={isProcessing}
              />
            </DialogBody>

            <DialogFooter>
              <Button variant="outline-secondary" onClick={onHide}>
                Cancel
              </Button>

              <ActionButton
                disabled={isProcessing}
                variant="primary"
                type="submit"
                isProcessing={isProcessing}
                isProcessingText="Adding comment"
              >
                {isUpdating ? 'Update' : 'Add'} comment
              </ActionButton>
            </DialogFooter>
          </Form>
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default AddOrUpdateCommentModal
